import React, { useState, useRef, useEffect } from 'react';
import { PlusIcon } from '@mc/wink-icons';
import cx from 'classnames';
import { ChevronDown, ChevronUp } from '@design-systems/icons';
import useId from '@mc/hooks/useId';
import { mcdsFlagCheck } from '@mc/wink/helpers/utils-ts';
import chainHandlers from '@mc/fn/chainHandlers';
import useJoinedRef from '@mc/hooks/useJoinedRef';
import ClusterLayout from '../ClusterLayout';
import Combobox, { ComboboxOption } from '../Combobox';
import Tag from '../Tag';
import Listbox from '../Listbox';
import Input from '../Input';
import Option from '../Listbox/Option';
import TextButton from '../TextButton';
import Button from '../Button';
import { TranslateTagInput } from './TranslateTagInput';
import stylesheet from './TagInput.css';

export const TagOption = (props: $TSFixMe) => {
  return mcdsFlagCheck('xp_mcds_redesign_components_organisms_tag_input') ? (
    <Option {...props} />
  ) : (
    <ComboboxOption {...props} />
  );
};

const includes = (tags: $TSFixMe, filter = '') => {
  return tags
    .map((tag: $TSFixMe) => tag.toLowerCase())
    .includes(filter.toLowerCase());
};

const defaultOnFilter = (tags: $TSFixMe, value = '') => {
  return tags.filter((tag: $TSFixMe) =>
    tag.toLowerCase().includes(value.toLowerCase()),
  );
};

const defaultOnMap = (tags: $TSFixMe, selectedTags = []) => {
  const set = new Set([...tags, ...selectedTags]);
  return [...set].sort();
};

type SelectedTagsProps = {
  hideSelectedTags?: boolean;
  label: React.ReactNode;
  onRemove: $TSFixMeFunction;
  selectedTags: $TSFixMe[];
  selectedTagsLimit?: number;
  outsideTags?: boolean;
};

const SelectedTags = ({
  hideSelectedTags = false,
  label,
  selectedTagsLimit,
  selectedTags,
  onRemove,
  outsideTags,
}: SelectedTagsProps) => {
  const labelId = useId();
  // If the limit isn't defined or a valid number, default allSelectedTagsVisible to true
  const [allSelectedTagsVisible, setAllSelectedTagsVisible] = useState(
    // @ts-expect-error TS(2345) FIXME: Argument of type 'number | undefined' is not assig... Remove this comment to see the full error message
    isNaN(selectedTagsLimit),
  );

  const visibleTags = allSelectedTagsVisible
    ? selectedTags
    : selectedTags.slice(0, selectedTagsLimit);
  // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
  const isOverLimit = selectedTags.length > selectedTagsLimit;
  const hiddenTagsCount = selectedTags.length - visibleTags.length;
  // Translate default text
  const { hiddenTagCountText, showFewerText, selectedTagText } =
    TranslateTagInput({
      count: hiddenTagsCount,
    });
  if (visibleTags.length === 0) {
    return null;
  }
  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement | HTMLSpanElement>,
    tag: string,
  ) => {
    const target = event.target as HTMLButtonElement;
    if (event.key === 'Backspace' && target.tagName === 'BUTTON') {
      onRemove(tag); // Remove the tag when Backspace is pressed
    }
  };

  return (
    <>
      {/* Selected tags are reached before the input. Announce the related label to screen readers. */}
      <span id={labelId} className="wink-visually-hidden">
        {label}
      </span>
      {/* Tags are in unordered list for screen readers to announce the number of selected tags 
      and navigate between lists. */}
      <ClusterLayout
        as="ul"
        gap={1}
        className={cx(
          stylesheet.selectedValues,
          { [stylesheet.outsideTags]: outsideTags },
          hideSelectedTags ? 'wink-visually-hidden' : '',
        )}
        aria-labelledby={labelId}
      >
        {visibleTags.map((selected) => (
          <li key={selected}>
            <Tag
              appearance="dismissible"
              onClick={() => onRemove(selected)}
              onKeyDown={(event) => handleKeyDown(event, selected)}
              size="small"
            >
              <span className="wink-visually-hidden">{selectedTagText}</span>
              {selected}
            </Tag>
          </li>
        ))}
        {isOverLimit &&
        mcdsFlagCheck('xp_mcds_redesign_components_organisms_tag_input') ? (
          <li key="hidden-tags">
            {hiddenTagsCount > 0 ? (
              <Button
                type="plain"
                onClick={() => setAllSelectedTagsVisible(true)}
                className={stylesheet.selectedLimitToggle}
                // eslint-disable-next-line formatjs/no-literal-string-in-jsx
              >
                + {hiddenTagCountText}
              </Button>
            ) : (
              <Button
                type="plain"
                onClick={() => setAllSelectedTagsVisible(false)}
                className={stylesheet.selectedLimitToggle}
              >
                {showFewerText}
              </Button>
            )}
          </li>
        ) : isOverLimit ? (
          <li>
            {hiddenTagsCount > 0 ? (
              <TextButton
                onClick={() => setAllSelectedTagsVisible(true)}
                className={stylesheet.selectedLimitToggle}
                // eslint-disable-next-line formatjs/no-literal-string-in-jsx
              >
                + {hiddenTagCountText}
              </TextButton>
            ) : (
              <TextButton
                onClick={() => setAllSelectedTagsVisible(false)}
                className={stylesheet.selectedLimitToggle}
              >
                {showFewerText}
              </TextButton>
            )}
          </li>
        ) : null}
      </ClusterLayout>
    </>
  );
};

export type InputListboxTriggerProps = {
  id?: string;
  isExpanded?: boolean;
  onBlur?: React.FocusEventHandler;
  onFilterChange: (val?: string) => void;
  onKeyDown?: (e: KeyboardEvent) => void;
  // Unused; used for emulating keyboard search for non-inputs
  onSelect?: (selectedValue?: string | number) => void;
  placeholder: React.ReactNode;
  renderSelectedValue?: (
    selected:
      | { children?: React.ReactNode; value: string }
      | { children?: React.ReactNode; value: string }[],
    placeholder: React.ReactNode,
  ) => string | readonly string[] | number | undefined;
  selected?: { children?: React.ReactNode; value: string }[];
  label?: React.ReactNode;
  autoFocus?: boolean;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  onChange: $TSFixMeFunction;
  size?: 'medium' | 'large';
  selectedTagsLimit?: number;
  value?: string[] | React.ReactNode[];
  hideSelectedTags?: boolean;
  onToggle: () => void;
  autohighlight?: boolean;
  selectedTags?: $TSFixMe[];
  onInputChange?: $TSFixMeFunction;
  tagPosition?: 'inside' | 'outside';
  filterTags?: $TSFixMeFunction;
  mapTags?: $TSFixMeFunction;
  filter?: string;
  setFilter?: (value: string) => void;
  readOnly?: boolean;
  disabled?: boolean;
  updateOptions?: (options: string) => void;
  optionsArray?: React.ReactNode[];
  filteredTags?: $TSFixMe[];
  initialExpanded?: boolean;
} & React.HTMLAttributes<HTMLInputElement>;

const InputListboxTrigger = React.forwardRef<
  HTMLInputElement,
  InputListboxTriggerProps
>(
  (
    {
      value = [],
      onChange = () => {},
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onSelect,
      size,
      selectedTagsLimit,
      hideSelectedTags = false,
      selected = [],
      onToggle,
      autohighlight = false,
      onBlur = () => {},
      tagPosition = 'outside',
      label,
      placeholder,
      filter,
      setFilter,
      readOnly = false,
      updateOptions,
      disabled,
      optionsArray = [],
      filteredTags = [],
      isExpanded,
      ...props
    },
    forwardedRef,
  ) => {
    const ref = useRef<HTMLInputElement>(null);
    const id = useId();
    const inputRef = useJoinedRef(forwardedRef, ref);
    const firstActiveDescendant = autohighlight ? 0 : -1;
    const [activeDescendant] = useState(firstActiveDescendant);
    const listboxId = `${id}-listbox`;
    const activeDescendantId = `${id}-active`;
    const shouldShowListbox = isExpanded;

    useEffect(() => {
      if (ref.current) {
        ref.current.focus();
      }
    }, [filter]);

    const remove = (valueToRemove: $TSFixMe) => {
      const result = selected.filter((v) => v !== valueToRemove);
      onChange(result);
    };

    const handleInputChange = (
      event: React.ChangeEvent<HTMLInputElement> | string,
    ) => {
      const filterValue =
        typeof event === 'string' ? event : event.target.value;
      // eslint-disable-next-line no-unused-expressions
      setFilter?.(filterValue); // Update the filter state in the parent
    };

    const tags = (
      <SelectedTags
        selectedTagsLimit={selectedTagsLimit}
        selectedTags={selected}
        // Pass label so screen readers announce it when reaching list of selected tags
        label={label}
        hideSelectedTags={hideSelectedTags || readOnly || disabled}
        onRemove={remove}
        outsideTags={tagPosition === 'outside'}
      />
    );

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && (filter?.trim()?.length ?? 0) > 0) {
        event.preventDefault();
        if (!value.includes(filter)) {
          const newValue = [...value, filter];
          onChange(newValue);

          // eslint-disable-next-line no-unused-expressions
          setFilter?.('');
          if (filter && updateOptions) {
            updateOptions(filter);
          }
        }
      }
    };

    return (
      <>
        <Input
          {...props}
          label={label}
          role="combobox"
          className={stylesheet.root}
          id={id}
          ref={inputRef}
          aria-haspopup="listbox"
          suffixText={isExpanded ? <ChevronUp /> : <ChevronDown />}
          aria-expanded={shouldShowListbox}
          aria-activedescendant={
            activeDescendant !== -1 && shouldShowListbox
              ? activeDescendantId
              : undefined
          }
          inputSize={size}
          aria-controls={shouldShowListbox ? listboxId : undefined}
          value={filter}
          disabled={disabled}
          inputMode={undefined}
          onChange={(event) => {
            handleInputChange(event);
            const shouldExpand =
              optionsArray.length > 0 ||
              filteredTags.length > 0 ||
              (filter && filter.trim().length > 0);
            if (shouldExpand && !isExpanded) {
              onToggle();
            }
          }}
          onKeyDown={handleKeyDown}
          onClick={() => {
            // Only expand if there are options, filtered tags, or if there's a filter value
            const shouldExpand =
              optionsArray.length > 0 ||
              filteredTags.length > 0 ||
              (filter && filter.trim().length > 0);
            if (shouldExpand) {
              onToggle();
            }
          }}
          onBlur={onBlur}
          prefixText={tagPosition === 'inside' && tags}
          placeholder={
            tagPosition === 'inside' && value
              ? ''
              : typeof placeholder === 'string'
              ? placeholder
              : undefined
          }
        />
        {tagPosition === 'outside' && <div>{tags}</div>}
      </>
    );
  },
);

export type TagInputProps = {
  'aria-autocomplete'?: 'none' | 'list';
  'aria-labelledby'?: string;
  children: React.ReactNode;
  disabled?: boolean;
  error?: string;
  filterTags?: $TSFixMeFunction;
  helpText?: React.ReactNode;
  hideCreateLabel?: boolean;
  hideLabel?: boolean;
  hideSelectedTags?: boolean;
  initialExpanded?: boolean;
  label?: React.ReactNode;
  mapTags?: $TSFixMeFunction;
  miscText?: React.ReactNode;
  onChange: $TSFixMeFunction;
  onFocus?: $TSFixMeFunction;
  onInputChange?: $TSFixMeFunction;
  readOnly?: boolean;
  selectedTagsLimit?: number;
  value?: string[];
  tagPosition?: 'inside' | 'outside';
  placeholder?: React.ReactNode;
  offset?: number;
};

const TagInput = React.forwardRef<$TSFixMe, TagInputProps>(function TagInput(
  {
    'aria-autocomplete': ariaAutocomplete = 'list',
    value = [],
    onChange,
    onInputChange = () => {},
    filterTags = defaultOnFilter,
    mapTags = defaultOnMap,
    selectedTagsLimit,
    children,
    hideCreateLabel = false,
    hideSelectedTags = false,
    initialExpanded = false,
    tagPosition = 'outside',
    label,
    readOnly = false,
    disabled,
    placeholder,
    offset,
    ...props
  },
  forwardedRef,
) {
  const redesigned = mcdsFlagCheck(
    'xp_mcds_redesign_components_organisms_tag_input',
  );

  const isBugFixesF_FlagOn = mcdsFlagCheck('xp_mcds_redesign_bug_fixes_F');
  const defaultOffset = isBugFixesF_FlagOn ? 4 : -16;

  const [filter, setFilter] = useState('');
  // Translate default text
  const { createText, addText } = TranslateTagInput({
    filter: filter,
  });

  const remove = (valueToRemove: $TSFixMe) => {
    onChange(value.filter((v) => v !== valueToRemove));
  };

  const toggle = (valueToToggle: $TSFixMe) => {
    if (valueToToggle.length > 0) {
      let nextValue;
      if (value.includes(valueToToggle)) {
        nextValue = value.filter((v) => v !== valueToToggle);
      } else {
        nextValue = [...value, valueToToggle];
      }
      onChange(nextValue);
    }
  };

  const handleInputChange = (filterValue: $TSFixMe) => {
    setFilter(filterValue);
    onInputChange(filterValue);
  };

  const [optionsArray, setOptionsArray] = useState(
    React.Children.toArray(children),
  );

  const addTagOption = (newTag: string) => {
    const newOption = redesigned ? (
      <TagOption key={`${newTag}-${Date.now()}`} value={newTag} />
    ) : (
      <TagOption key={newTag} value={newTag} />
    );
    setOptionsArray((prevOptions) => [...prevOptions, newOption]);
    setFilter('');
  };

  const filteredTags = filterTags(
    mapTags(
      (redesigned ? (optionsArray as $TSFixMe) : (children as $TSFixMe)).map(
        (child: $TSFixMe) => child.props.value,
      ),
      value,
    ),
    filter,
  );

  return (
    <React.Fragment>
      {redesigned ? (
        <>
          <Listbox
            multiple
            matchTargetWidth
            initialExpanded={initialExpanded}
            // @ts-expect-error TS(2739) FIXME: Type 'ForwardRefExoticComponent<SelectInlineListbo... Remove this comment to see the full error message
            trigger={(triggerProps) => {
              return (
                <InputListboxTrigger
                  {...triggerProps}
                  tagPosition={tagPosition}
                  label={label}
                  placeholder={placeholder}
                  selectedTagsLimit={selectedTagsLimit}
                  onChange={onChange}
                  updateOptions={addTagOption}
                  filter={filter}
                  setFilter={setFilter}
                  readOnly={readOnly}
                  disabled={disabled}
                  selected={value}
                  value={value}
                  optionsArray={optionsArray}
                  filteredTags={filteredTags}
                  hideSelectedTags={hideSelectedTags}
                  initialExpanded={initialExpanded}
                />
              );
            }}
            disabled={disabled}
            onChange={(selectedValue) => {
              if (Array.isArray(selectedValue)) {
                if (filter.length > 0) {
                  const newValue = [...value, ...selectedValue];
                  onChange(newValue);
                  setFilter('');
                } else {
                  onChange(selectedValue);
                }
              }
            }}
            ref={forwardedRef}
            value={value}
            tags={optionsArray}
            offset={offset ? offset : defaultOffset}
            {...props}
          >
            {!hideCreateLabel &&
              filter.length > 0 &&
              !includes(filteredTags, filter) && (
                <TagOption
                  key="create"
                  value={filter}
                  label={label}
                  hide
                  addOption
                >
                  <PlusIcon className={stylesheet.icon} />
                  {addText}
                </TagOption>
              )}
            {filteredTags.map((tag: $TSFixMe, index: number) => (
              <TagOption
                key={`${tag}-${index}`}
                value={tag}
                aria-selected={value.includes(tag)}
                label={label}
              />
            ))}
          </Listbox>
        </>
      ) : (
        <Combobox
          {...props}
          ref={forwardedRef}
          aria-autocomplete={ariaAutocomplete}
          aria-multiselectable={true}
          autohighlight
          value={filter}
          label={label}
          onChange={handleInputChange}
          onSelect={(selectedValue) => {
            toggle(selectedValue);
            handleInputChange('');
          }}
          closeOnSelect={false}
          // Refreshes Popup whenever value changes.
          unsafe_key={value.join('/')}
          // @ts-expect-error TS(2322) FIXME: Type '{ children: any[]; ref: ForwardedRef<any>; "... Remove this comment to see the full error message
          unsafe_renderExtraContent={
            <SelectedTags
              selectedTagsLimit={selectedTagsLimit}
              selectedTags={value}
              onRemove={remove}
              // Pass label so screen readers announce it when reaching list of selected tags
              label={label}
              hideSelectedTags={hideSelectedTags}
            />
          }
        >
          {!hideCreateLabel &&
            filter.length > 0 &&
            !includes(filteredTags, filter) && (
              <TagOption key="create" value={filter}>
                <PlusIcon className={stylesheet.icon} />
                {createText}
              </TagOption>
            )}
          {filteredTags.map((tag: $TSFixMe) => (
            <TagOption
              key={tag}
              value={tag}
              aria-selected={value.includes(tag)}
            />
          ))}
        </Combobox>
      )}
    </React.Fragment>
  );
});

export default TagInput;
